// BacktestRunner — Run backtests + show results (Angel One light theme)
import React, { useState } from 'react';
import { useCandleData } from '../../../hooks/useMarketData';
import PnLBadge from '../shared/PnLBadge';
import { FaPlay } from 'react-icons/fa';

export default function BacktestRunner({ strategies, onRun, results }) {
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [running, setRunning] = useState(false);

  const toDate = new Date().toISOString().slice(0, 19).replace('T', ' ');
  const fromDate = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return d.toISOString().slice(0, 19).replace('T', ' ');
  })();

  const { candles } = useCandleData(
    selectedStrategy?.instrument?.exchange || 'NSE',
    selectedStrategy?.instrument?.token || '',
    'FIFTEEN_MINUTE',
    fromDate,
    toDate
  );

  const handleRun = async () => {
    if (!selectedStrategy || candles.length === 0) return;
    setRunning(true);
    await onRun(selectedStrategy, candles);
    setRunning(false);
  };

  return (
    <div className="space-y-4">
      {/* Strategy Selection */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
        <h4 className="text-[11px] font-medium text-gray-500 mb-2">Run Backtest</h4>
        <div className="flex items-center gap-3">
          <select
            value={selectedStrategy?.id || ''}
            onChange={e => setSelectedStrategy(strategies.find(s => s.id === e.target.value))}
            className="flex-1 bg-white border border-gray-300 rounded-lg px-3 py-2 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select strategy...</option>
            {strategies.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          <button
            onClick={handleRun}
            disabled={!selectedStrategy || running}
            className="px-4 py-2 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            <FaPlay className="text-[8px]" />
            {running ? 'Running...' : 'Run'}
          </button>
        </div>
      </div>

      {/* Results */}
      {results && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <ResultCard label="Total P&L" value={<PnLBadge value={results.totalPnL} size="md" />} />
            <ResultCard label="Win Rate" value={`${results.winRate.toFixed(1)}%`} />
            <ResultCard label="Trades" value={`${results.totalTrades} (${results.wins}W / ${results.losses}L)`} />
            <ResultCard label="Max Drawdown" value={`${results.maxDrawdown.toFixed(1)}%`} />
          </div>

          {/* Trade History */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
            <div className="grid grid-cols-6 gap-2 px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200 bg-gray-50">
              <div>Entry Time</div>
              <div className="text-right">Entry</div>
              <div>Exit Time</div>
              <div className="text-right">Exit</div>
              <div className="text-right">Qty</div>
              <div className="text-right">P&L</div>
            </div>
            <div className="max-h-60 overflow-y-auto">
              {results.trades.map((trade, i) => (
                <div key={i} className="grid grid-cols-6 gap-2 px-4 py-2 border-b border-gray-50 text-[11px]">
                  <div className="text-gray-500">{trade.entryTime?.split(' ')[1]?.slice(0, 5) || '—'}</div>
                  <div className="text-right font-mono text-gray-900">₹{trade.entry?.toFixed(2)}</div>
                  <div className="text-gray-500">{trade.exitTime?.split(' ')[1]?.slice(0, 5) || '—'}</div>
                  <div className="text-right font-mono text-gray-900">₹{trade.exit?.toFixed(2)}</div>
                  <div className="text-right font-mono text-gray-900">{trade.qty}</div>
                  <div className="text-right">
                    <PnLBadge value={trade.pnl} size="sm" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ResultCard({ label, value }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-3">
      <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1">{label}</p>
      <div className="text-sm font-semibold text-gray-900">{value}</div>
    </div>
  );
}
